import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { createStyles, InputAdornment, MuiThemeProvider, Theme, withStyles, WithStyles } from '@material-ui/core';
import { Email as EmailIcon, Smartphone as SmartphoneIcon } from '@material-ui/icons';
import { graphql } from 'babel-plugin-relay/macro';
import { get, has } from 'lodash';

import {
  Button,
  DialogPatientContact,
  PatientContactTemp,
  Popper,
  PopperProps,
  TextField,
  Typography,
} from 'components/v2';
import { muiThemeLumaDark } from 'themes';

import { PopperPatientContactSelect_Patient$key } from './__generated__/PopperPatientContactSelect_Patient.graphql';
import ListPatientContacts, { PatientContact } from './ListPatientContacts';

const messages = defineMessages({
  title: {
    id: 'deprecated.fragments.PopperPatientContactSelect.title',
    defaultMessage: 'Choose contact method',
  },
  titleOneTimeContact: {
    id: 'deprecated.fragments.PopperPatientContactSelect.titleOneTimeContact',
    defaultMessage: 'Send to this contact once',
  },
  createOneTimeContact: {
    id: 'deprecated.fragments.PopperPatientContactSelect.createOneTimeContact',
    defaultMessage: 'Create one time contact',
  },
  editOneTimeContact: {
    id: 'deprecated.fragments.PopperPatientContactSelect.editOneTimeContact',
    defaultMessage: 'Edit contact',
  },
  revertOneTimeContact: {
    id: 'deprecated.fragments.PopperPatientContactSelect.revertOneTimeContact',
    defaultMessage: 'Revert to default',
  },
  or: {
    id: 'deprecated.fragments.PopperPatientContactSelect.or',
    defaultMessage: 'or',
  },
  sms: {
    id: 'deprecated.fragments.PopperPatientContactSelect.sms',
    defaultMessage: 'SMS',
  },
  email: {
    id: 'deprecated.fragments.PopperPatientContactSelect.email',
    defaultMessage: 'Email',
  },
  whatsapp: {
    id: 'deprecated.fragments.PopperPatientContactSelect.whatsapp',
    defaultMessage: 'WhatsApp',
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      height: 'initial !important',
    },
    popperContent: {
      padding: `${unit * 2}px ${unit * 2}px ${unit}px ${unit * 2}px`,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxHeight: '100%',
    },
    actionsWrapper: {
      marginTop: unit,
      marginLeft: -1 * unit,
      display: 'flex',
      alignItems: 'center',
    },
  });

export type PatientContactCustom = PatientContact | PatientContactTemp;

export interface PopperPatientContactSelectProps
  extends Omit<PopperProps, 'classes' | 'children'>,
    WithStyles<typeof styles> {
  fragmentRef: PopperPatientContactSelect_Patient$key;
  selectedContacts?: PatientContactCustom[];
  onContactsSelected: (patientContact?: PatientContactCustom[]) => void;
}

const PopperPatientContactSelect: React.FC<PopperPatientContactSelectProps> = ({
  classes,
  fragmentRef,
  selectedContacts,
  onContactsSelected,
  ...rest
}) => {
  const patient = useFragment<PopperPatientContactSelect_Patient$key>(
    graphql`
      fragment PopperPatientContactSelect_Patient on Patient {
        ...ListPatientContacts_Patient
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);

  const oneTimeContact = useMemo(() => {
    const firstContact: PatientContactCustom | undefined = get(selectedContacts, 0);
    return firstContact && !has(firstContact, '_id') ? (firstContact as PatientContactTemp) : undefined;
  }, [selectedContacts]);

  const handleOneTimeContact = useCallback(
    (patientContact: PatientContactTemp) => {
      setOpen(false);
      onContactsSelected([patientContact]);
    },
    [setOpen, onContactsSelected],
  );

  return (
    <>
      {oneTimeContact ? (
        <MuiThemeProvider theme={muiThemeLumaDark}>
          <Popper className={classes.root} {...rest}>
            <div className={classes.popperContent}>
              <Typography variant="body1Large" paragraph>
                {formatMessage(messages.titleOneTimeContact)}
              </Typography>
              <Typography variant="body1Bold" gutterBottom>
                {formatMessage(messages[oneTimeContact.type as keyof typeof messages])}
              </Typography>
              <TextField
                value={oneTimeContact.value as string}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {oneTimeContact.type === 'email' ? (
                        <EmailIcon fontSize="small" />
                      ) : (
                        <SmartphoneIcon fontSize="small" />
                      )}
                    </InputAdornment>
                  ),
                }}
                disabled
                fullWidth
              />
              <Typography className={classes.actionsWrapper}>
                <Button color="primary" underline onClick={() => setOpen(true)}>
                  {formatMessage(messages.editOneTimeContact)}
                </Button>
                {formatMessage(messages.or)}
                <Button color="primary" underline onClick={() => onContactsSelected(undefined)}>
                  {formatMessage(messages.revertOneTimeContact)}
                </Button>
              </Typography>
            </div>
          </Popper>
        </MuiThemeProvider>
      ) : (
        <Popper className={classes.root} {...rest}>
          <div className={classes.popperContent}>
            <Typography variant="body1Large" paragraph>
              {formatMessage(messages.title)}
            </Typography>
            <ListPatientContacts
              fragmentRef={patient}
              selectedContacts={selectedContacts as PatientContact[]}
              onContactsSelected={onContactsSelected}
            />
            <div className={classes.actionsWrapper}>
              <Button color="primary" underline onClick={() => setOpen(true)}>
                {formatMessage(messages.createOneTimeContact)}
              </Button>
            </div>
          </div>
        </Popper>
      )}
      <DialogPatientContact
        open={open}
        initialValues={oneTimeContact}
        onClose={() => setOpen(false)}
        onSubmit={handleOneTimeContact}
      />
    </>
  );
};

export default withStyles(styles)(PopperPatientContactSelect);
